import axios from "axios";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { Container } from "reactstrap";
import styled from "styled-components";
import AddressAutocomplete from "../../components/AddressAutocomplete/AddressAutocomplete";
import NewFooter from "../../components/Footers/NewFooter";
import ImageUploader from "../../components/ImageUploader/ImageUploader";
import InputDocumentUploader from "../../components/InputDocumentUploader/InputDocumentUploader";
import NewNavbar from "../../components/Navbar/NewNavbar";
import { PageContainer, PrimaryButton } from "../../components/styled/shared";
import { API_URL } from "../../constants";
import { useAccount } from "../../contexts/AccountContext";
import useIntakeCooldown from "../../hooks/useIntakeCooldown";
import {
  createManualListing,
  deleteImage,
  fetchListing,
  uploadImages,
} from "../../redux/actions/assessment";
import { listingAuthHeader } from "../../utils/auth";
import { flattenError } from "../../utils/errorDisplay";
import { currentIntakeRetryAt, intakeErrorText } from "../../utils/intakeLimits";
import { storageGet, storageRemove } from "../../utils/storage";

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC",
];

const HOME_TYPES = ["Single-family", "Condo", "Townhouse", "Multi-family", "Other"];

// Zillow homeType values → our HOME_TYPES options.
const HOME_TYPE_FROM_ZILLOW = {
  SINGLE_FAMILY: "Single-family",
  CONDO: "Condo",
  TOWNHOUSE: "Townhouse",
  MULTI_FAMILY: "Multi-family",
};

const MainContent = styled.div`
  flex: 1;
  padding: 120px 0 60px;

  @media (max-width: 767.98px) {
    padding: 100px 0 40px;
  }
`;

const PageTitle = styled.h1`
  font-size: 48px;
  font-weight: 700;
  color: #1a1a1a;
  text-align: center;
  margin-bottom: 16px;

  @media (max-width: 767.98px) {
    font-size: 36px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 18px;
  color: #6b7280;
  text-align: center;
  max-width: 700px;
  margin: 0 auto 48px;
  line-height: 1.6;
`;

const FormCard = styled.div`
  max-width: 700px;
  margin: 0 auto;
  background: white;
  border: none;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;

  form {
    border: none;
    outline: none;
    box-shadow: none;
  }

  @media (max-width: 767.98px) {
    padding: 24px;
  }
`;

const SectionLabel = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 24px;
`;

/** @type {import('styled-components').StyledComponent<'div', any, { $columns?: string }, never>} */
const FieldRow = styled.div`
  display: grid;
  grid-template-columns: ${(/** @type {{ $columns?: string }} */ props) => props.$columns || "1fr"};
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 767.98px) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  height: 44px;
  padding: 0 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 15px;
  color: #1a1a1a;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #6b7280;
    box-shadow: 0 0 0 3px rgba(107, 114, 128, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const Select = styled.select`
  width: 100%;
  height: 44px;
  padding: 0 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 15px;
  color: #1a1a1a;
  background: white;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #6b7280;
    box-shadow: 0 0 0 3px rgba(107, 114, 128, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 15px;
  color: #1a1a1a;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #6b7280;
    box-shadow: 0 0 0 3px rgba(107, 114, 128, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const FieldHint = styled.span`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
`;

const OptionalBadge = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #9ca3af;
  margin-left: 6px;
`;

const SectionDivider = styled.div`
  border-top: 1px solid #e5e7eb;
  margin: 24px 0 20px;
  padding-top: 4px;
`;

const SubmitButton = styled(PrimaryButton)`
  display: block;
  width: 100%;
  height: 52px;
  font-size: 16px;
  margin-top: 24px;
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  color: #991b1b;
  font-size: 14px;
`;

const FieldError = styled.span`
  font-size: 12px;
  color: #dc2626;
  margin-top: 4px;
`;

const PropertySummary = styled.div`
  background: #f3f4f6;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 24px;
  font-size: 15px;
  color: #374151;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const SummaryItem = styled.span`
  font-weight: 500;
`;

// Trello #52: the quiet way past step 2 — a link, never the primary action.
const SkipPhotosRow = styled.div`
  margin-top: 12px;
  font-size: 14px;
  color: #6b7280;

  button {
    padding: 0;
    border: 0;
    background: none;
    color: #4b5563;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;

    &:disabled {
      cursor: default;
      opacity: 0.6;
    }
  }
`;

const AltLink = styled.div`
  text-align: center;
  margin-top: 24px;
  font-size: 14px;
  color: #6b7280;

  a {
    color: #4b5563;
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const StepIndicator = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 32px;
`;

const StepDotRow = styled.div`
  display: flex;
  gap: 12px;
`;

const StepLabel = styled.div`
  font-size: 0.85rem;
  color: #6b7280;
  font-weight: 500;
`;

/** @type {import('styled-components').StyledComponent<'div', any, { $active?: boolean }, never>} */
const StepDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(/** @type {{ $active?: boolean }} */ props) => (props.$active ? "var(--brand-primary, #0e7e7a)" : "#d1d5db")};
  transition: background 0.3s;
`;

const EMPTY_FORM = {
  streetAddress: "",
  city: "",
  addressState: "",
  zipCode: "",
  bedrooms: "",
  bathrooms: "",
  livingArea: "",
  lotSize: "",
  lotSizeUnit: "sqft",
  askingPrice: "",
  yearBuilt: "",
  homeType: "",
  email: "",
  description: "",
  sellerNotes: "",
};

function ManualSubmitPage() {
  const intakeCooldown = useIntakeCooldown();
  const [step, setStep] = useState(1);
  const { account, signedIn } = useAccount();
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [prefillNote, setPrefillNote] = useState("");
  const [fieldErrors, setFieldErrors] = useState(/** @type {Record<string, string>} */ ({}));
  const [listingUuid, setListingUuid] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [skippingPhotos, setSkippingPhotos] = useState(false);
  const [skipPhotosError, setSkipPhotosError] = useState(/** @type {string | null} */ (null));
  const hydrationRequestIdRef = useRef("");
  const hydrationRetryInFlightRef = useRef(false);
  const creationInFlightRef = useRef(false);
  const focusStreetAddressAfterPrefillRef = useRef(false);
  const streetAddressInputRef = useRef(/** @type {HTMLInputElement | null} */ (null));

  const dispatch = useDispatch();
  const history = useHistory();
  const activeListingId = useSelector((/** @type {any} */ s) => s.listings.activeListingId);
  const currentListing = useSelector((/** @type {any} */ s) => s.listings.currentListing);
  const globalLoading = useSelector((/** @type {any} */ s) => s.listings.globalLoading);
  const error = useSelector((/** @type {any} */ s) => s.listings.error);
  const confirmationLoading = useSelector((/** @type {any} */ s) => s.listings.confirmationLoading);
  const confirmationError = useSelector((/** @type {any} */ s) => s.listings.confirmationError);
  const imageUploadLoading = useSelector((/** @type {any} */ s) => s.listings.imageUploadLoading);
  const imageUploadError = useSelector((/** @type {any} */ s) => s.listings.imageUploadError);
  const activeListingIdRef = useRef(activeListingId);
  const currentListingRef = useRef(currentListing);
  const globalLoadingRef = useRef(globalLoading);
  const confirmationLoadingRef = useRef(confirmationLoading);
  activeListingIdRef.current = activeListingId;
  currentListingRef.current = currentListing;
  globalLoadingRef.current = globalLoading;
  confirmationLoadingRef.current = confirmationLoading;

  useEffect(() => {
    if (!focusStreetAddressAfterPrefillRef.current || !form.streetAddress) return;
    focusStreetAddressAfterPrefillRef.current = false;
    streetAddressInputRef.current?.focus();
  }, [form.streetAddress]);

  const listingNeedsHydration = Boolean(
    activeListingId && currentListing?.uuid !== activeListingId
  );

  // A UUID means creation succeeded OR this tab restored an in-progress manual
  // listing from sessionStorage. In both cases resume at photos; requiring a
  // fresh null → UUID transition sent refreshed/back-navigation users to a
  // blank step 1 and could create duplicate listings.
  // useLayoutEffect (not useEffect) so the step transition commits
  // synchronously with the Redux update — matches the old
  // componentDidUpdate timing. Keeps existing tests passing without
  // needing waitFor wrappers around store.dispatch.
  useLayoutEffect(() => {
    // Cancellation completes asynchronously after the confirmation page has
    // already navigated back here. Once Redux clears the active listing, drop
    // every local reference to the cancelled draft and restore the details
    // step instead of leaving the user on a photo uploader for a deleted UUID.
    if (!activeListingId) {
      hydrationRequestIdRef.current = "";
      if (step !== 1) setStep(1);
      if (listingUuid) setListingUuid("");
      return;
    }
    if (step !== 1) return;
    // Hydrate first, decide second: on a mid-flow refresh the listing isn't
    // in Redux yet, so fetch it before choosing whether to resume Step 2.
    if (!currentListing || currentListing.uuid !== activeListingId) {
      // One automatic request per restored UUID. A transient failure keeps the
      // UUID so the explicit Try again control can retry without an effect
      // loop; a terminal 403/404 clears it in the reducer and exits this gate.
      if (hydrationRequestIdRef.current !== activeListingId && !confirmationLoading) {
        hydrationRequestIdRef.current = activeListingId;
        dispatch(fetchListing(activeListingId));
      }
      return;
    }
    // Resume photo upload ONLY for this flow's own manual listing. A Zillow
    // import (or a paid listing) surviving in the tab must not hijack /submit
    // and attach photos to the wrong property.
    if (currentListing.source !== "manual") {
      return;
    }
    if (currentListing.state && currentListing.state !== "preparing") {
      history.replace("/dashboard");
      return;
    }
    if (currentListing.user_confirmed) {
      history.replace("/listing-confirmation/");
      return;
    }
    setStep(2);
    setListingUuid(activeListingId);
  }, [activeListingId, confirmationLoading, currentListing, dispatch, history, listingUuid, step]);

  const validateForm = () => {
    /** @type {Record<string, string>} */
    const errors = {};
    const {
      streetAddress,
      city,
      addressState,
      zipCode,
      bedrooms,
      bathrooms,
      livingArea,
      lotSize,
      email,
      yearBuilt,
    } = form;

    if (!streetAddress.trim()) errors.streetAddress = "Required";
    if (!city.trim()) errors.city = "Required";
    if (!addressState) errors.addressState = "Required";
    if (!zipCode.trim()) {
      errors.zipCode = "Required";
    } else if (!/^\d{5}(-\d{4})?$/.test(zipCode.trim())) {
      errors.zipCode = "Enter a valid ZIP code";
    }
    if (!bedrooms.trim()) {
      errors.bedrooms = "Required";
    } else if (Number(bedrooms) < 0 || Number(bedrooms) > 50) {
      errors.bedrooms = "Enter a valid number";
    }
    if (!bathrooms.trim()) {
      errors.bathrooms = "Required";
    } else if (Number(bathrooms) < 0 || Number(bathrooms) > 50) {
      errors.bathrooms = "Enter a valid number";
    }
    if (!livingArea.trim()) {
      errors.livingArea = "Required";
    } else if (Number(livingArea) < 1 || Number(livingArea) > 100000) {
      errors.livingArea = "Enter a valid square footage";
    }
    // A condo or apartment has no lot, and demanding one made those listings
    // impossible to submit at all (Trello #64). Blank is allowed; a value that
    // IS given still has to be a real one.
    if (lotSize.trim()) {
      const maxForUnit = form.lotSizeUnit === "acres" ? 1148 : 50000000;
      const minForUnit = form.lotSizeUnit === "acres" ? 0.01 : 1;
      if (Number(lotSize) < minForUnit || Number(lotSize) > maxForUnit) {
        errors.lotSize = `Enter a valid lot size in ${form.lotSizeUnit === "acres" ? "acres" : "square feet"}`;
      }
    }
    // A signed-in agent already gave us an address when they made the account;
    // asking again was pure friction (Trello #65). Typed values are still
    // checked, so sending the prep somewhere else stays possible.
    if (!email.trim()) {
      if (!signedIn) errors.email = "Required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errors.email = "Enter a valid email address";
    }
    if (
      yearBuilt &&
      (Number(yearBuilt) < 1600 || Number(yearBuilt) > new Date().getFullYear() + 1)
    ) {
      errors.yearBuilt = "Enter a valid year";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /** @param {React.FormEvent} e */
  const handleSubmitForm = (e) => {
    e.preventDefault();
    const activeId = activeListingIdRef.current;
    const activeListing = currentListingRef.current;
    const replacingKnownNonManualListing =
      activeId && activeListing?.uuid === activeId && activeListing.source !== "manual";
    // A restored manual listing must be reconciled before Step 1 can create
    // anything. Refs make this guard immediate even when a fast second click
    // still holds a handler from the previous render.
    if (
      globalLoadingRef.current ||
      creationInFlightRef.current ||
      currentIntakeRetryAt() > Date.now() ||
      (activeId && !replacingKnownNonManualListing)
    ) {
      return;
    }
    if (!validateForm()) return;

    /** @type {Record<string, string | number>} */
    const data = {
      street_address: form.streetAddress.trim(),
      city: form.city.trim(),
      address_state: form.addressState,
      zipcode: form.zipCode.trim(),
      bedrooms: Number(form.bedrooms),
      bathrooms: Number(form.bathrooms),
      living_area: Number(form.livingArea),
    };

    // Omitted entirely when there is no lot, rather than sent as a zero that
    // would read as a measured value of nothing.
    if (form.lotSize.trim()) {
      data.lot_size =
        form.lotSizeUnit === "acres"
          ? Math.round(Number(form.lotSize) * 43560)
          : Number(form.lotSize);
    }

    if (form.askingPrice) data.price = Number(form.askingPrice);
    if (form.yearBuilt) data.year_built = Number(form.yearBuilt);
    if (form.homeType) data.home_type = form.homeType;
    data.email_address = form.email.trim() || (signedIn ? (account?.email || "").trim() : "");
    if (form.description.trim()) data.description = form.description.trim();
    if (form.sellerNotes.trim()) data.seller_notes = form.sellerNotes.trim();

    creationInFlightRef.current = true;
    const request = dispatch(createManualListing(data));
    Promise.resolve(request).finally(() => {
      creationInFlightRef.current = false;
    });
  };

  /** @param {File[]} files */
  const handleImageUpload = (files) => {
    return dispatch(
      uploadImages(listingUuid, files, (progress) => {
        setUploadProgress(progress);
      })
    );
  };

  /** @param {number|string} imageId */
  const handleImageDelete = (imageId) => {
    dispatch(deleteImage(listingUuid, imageId));
  };

  /**
   * Apply a looked-up property (and/or the bare suggestion) to the form.
   * Pre-fills from the property's most recent Zillow record — square footage,
   * lot size, and bedroom counts rarely change between listings. Deliberately
   * NOT pre-filled: asking price (an old sale price is not an asking price)
   * and description (describes the previous listing's condition).
   * @param {Record<string, any> | null} property
   * @param {{display: string, city: string, state: string, zipcode: string} | null} suggestion
   */
  const applyPropertyPrefill = (property, suggestion) => {
    const fallback = suggestion || { display: "", city: "", state: "", zipcode: "" };
    setForm((prev) => ({
      ...prev,
      streetAddress:
        property?.street_address || fallback.display.split(",")[0] || prev.streetAddress,
      city: property?.city || fallback.city || prev.city,
      addressState: property?.state || fallback.state || prev.addressState,
      zipCode: property?.zipcode || fallback.zipcode || prev.zipCode,
      ...(property
        ? {
            bedrooms: property.bedrooms != null ? String(property.bedrooms) : prev.bedrooms,
            bathrooms: property.bathrooms != null ? String(property.bathrooms) : prev.bathrooms,
            livingArea:
              property.living_area != null
                ? String(Math.round(property.living_area))
                : prev.livingArea,
            lotSize:
              property.lot_size != null ? String(Math.round(property.lot_size)) : prev.lotSize,
            lotSizeUnit: property.lot_size != null ? "sqft" : prev.lotSizeUnit,
            yearBuilt: property.year_built != null ? String(property.year_built) : prev.yearBuilt,
            homeType:
              HOME_TYPE_FROM_ZILLOW[
                /** @type {keyof typeof HOME_TYPE_FROM_ZILLOW} */ (property.home_type)
              ] || prev.homeType,
          }
        : {}),
    }));
    if (property) {
      setPrefillNote("Pre-filled from available records.");
    }
  };

  /** @param {{display: string, zpid: number, city: string, state: string, zipcode: string}} suggestion */
  const handleAddressSelected = (suggestion) => {
    setPrefillNote("");
    // Selecting a result replaces and unmounts the autocomplete. Move focus
    // to the first populated form field after that commit so keyboard users
    // continue in a predictable place instead of dropping to the document.
    focusStreetAddressAfterPrefillRef.current = true;
    axios
      .get(`${API_URL}/address-lookup/`, { params: { address: suggestion.display } })
      .then(({ data }) => applyPropertyPrefill(data.property || {}, suggestion))
      .catch(() => applyPropertyPrefill(null, suggestion));
  };

  // A property picked on the homepage hero arrives here pre-resolved.
  // biome-ignore lint/correctness/useExhaustiveDependencies: one-shot mount handoff
  useEffect(() => {
    const pending = storageGet("pendingAddressPrefill");
    if (!pending) return;
    storageRemove("pendingAddressPrefill");
    try {
      const { property, suggestion } = JSON.parse(pending);
      applyPropertyPrefill(property, suggestion);
    } catch {
      // Malformed handoff — ignore; the user just fills the form normally.
    }
  }, []);

  const handleContinueToReview = () => {
    // activeListingId has been set in Redux since step 1 completed.
    history.push("/listing-confirmation/");
  };

  // Trello #52: mark the listing as prepared without photos, then continue to
  // review exactly as the upload path does. Same auth as the uploads (owner,
  // or the funnel token this browser holds for the listing).
  const handleSkipPhotos = async () => {
    if (!listingUuid || skippingPhotos) return;
    setSkipPhotosError(null);
    setSkippingPhotos(true);
    try {
      await axios.post(
        `${API_URL}/listings/${listingUuid}/skip-photos/`,
        {},
        { headers: listingAuthHeader(listingUuid) }
      );
      history.push("/listing-confirmation/");
    } catch (error) {
      console.error(
        "Skip photos failed:",
        /** @type {any} */ (error)?.response?.data || /** @type {any} */ (error)?.message
      );
      setSkipPhotosError("Couldn't skip photos — try again.");
      setSkippingPhotos(false);
    }
  };

  const handleRetryListingHydration = () => {
    const listingId = activeListingIdRef.current;
    if (!listingId || confirmationLoadingRef.current || hydrationRetryInFlightRef.current) return;
    hydrationRetryInFlightRef.current = true;
    hydrationRequestIdRef.current = listingId;
    const request = dispatch(fetchListing(listingId));
    Promise.resolve(request).finally(() => {
      hydrationRetryInFlightRef.current = false;
    });
  };

  /** @param {string} field */
  const renderFieldError = (field) => {
    const err = fieldErrors[field];
    return err ? <FieldError>{err}</FieldError> : null;
  };

  /** @param {string} field @param {string} value */
  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const renderStep1 = () => {
    const intakeMessage = intakeErrorText(error, intakeCooldown.retryAt);
    return (
      <form onSubmit={handleSubmitForm}>
        {(error || intakeMessage) && (
          <ErrorMessage>
            {(intakeMessage ? [{ field: null, message: intakeMessage }] : flattenError(error)).map(
              ({ field, message }, i) => (
                // Index is fine as key — list is rebuilt on each render
                // from a fresh `error` payload, no reordering.
                // biome-ignore lint/suspicious/noArrayIndexKey: see above
                <div key={i}>{field ? `${field}: ${message}` : message}</div>
              )
            )}
          </ErrorMessage>
        )}

        <SectionLabel>Property details</SectionLabel>

        {/* Users arrive here directly OR redirected from the homepage hero
            with the form already filled. Showing an empty "Start with an
            address" box above a populated form just asks them to do the thing
            they already did, so it only appears while there is no address
            yet. The prefill note stays either way — it explains where the
            values below came from. */}
        {!form.streetAddress && (
          <FieldRow $columns="1fr">
            <FieldGroup>
              <Label htmlFor="addressLookup">Start with an address</Label>
              <AddressAutocomplete
                id="addressLookup"
                placeholder="Enter any address… Listino fills in the details"
                onSelect={handleAddressSelected}
              />
            </FieldGroup>
          </FieldRow>
        )}
        {prefillNote && (
          <FieldRow $columns="1fr">
            <FieldGroup>
              <FieldHint style={{ display: "block" }}>{prefillNote}</FieldHint>
            </FieldGroup>
          </FieldRow>
        )}

        <FieldRow $columns="1fr">
          <FieldGroup>
            <Label htmlFor="streetAddress">Street address *</Label>
            <Input
              id="streetAddress"
              ref={streetAddressInputRef}
              value={form.streetAddress}
              onChange={(e) => updateField("streetAddress", e.target.value)}
              placeholder="123 Main St"
              disabled={globalLoading}
            />
            {renderFieldError("streetAddress")}
          </FieldGroup>
        </FieldRow>

        <FieldRow $columns="1fr 1fr 1fr">
          <FieldGroup>
            <Label htmlFor="city">City *</Label>
            <Input
              id="city"
              value={form.city}
              onChange={(e) => updateField("city", e.target.value)}
              placeholder="City"
              disabled={globalLoading}
            />
            {renderFieldError("city")}
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor="addressState">State *</Label>
            <Select
              id="addressState"
              value={form.addressState}
              onChange={(e) => updateField("addressState", e.target.value)}
              disabled={globalLoading}
            >
              <option value="">Select</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
            {renderFieldError("addressState")}
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor="zipCode">ZIP code *</Label>
            <Input
              id="zipCode"
              value={form.zipCode}
              onChange={(e) => updateField("zipCode", e.target.value)}
              placeholder="12345"
              disabled={globalLoading}
            />
            {renderFieldError("zipCode")}
          </FieldGroup>
        </FieldRow>

        <FieldRow $columns="1fr 1fr">
          <FieldGroup>
            <Label htmlFor="bedrooms">Bedrooms *</Label>
            <Input
              id="bedrooms"
              type="number"
              min="0"
              value={form.bedrooms}
              onChange={(e) => updateField("bedrooms", e.target.value)}
              placeholder="3"
              disabled={globalLoading}
            />
            {renderFieldError("bedrooms")}
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor="bathrooms">Bathrooms *</Label>
            <Input
              id="bathrooms"
              type="number"
              min="0"
              step="0.5"
              value={form.bathrooms}
              onChange={(e) => updateField("bathrooms", e.target.value)}
              placeholder="2"
              disabled={globalLoading}
            />
            {renderFieldError("bathrooms")}
          </FieldGroup>
        </FieldRow>

        <FieldRow $columns="1fr 1fr">
          <FieldGroup>
            <Label htmlFor="livingArea">Living area (sqft) *</Label>
            <Input
              id="livingArea"
              type="number"
              min="1"
              value={form.livingArea}
              onChange={(e) => updateField("livingArea", e.target.value)}
              placeholder="1500"
              disabled={globalLoading}
            />
            {renderFieldError("livingArea")}
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor="lotSize">Lot size</Label>
            <div style={{ display: "flex", gap: "8px" }}>
              <Input
                id="lotSize"
                type="number"
                min="0.01"
                step="any"
                style={{ flex: 1 }}
                value={form.lotSize}
                onChange={(e) => updateField("lotSize", e.target.value)}
                placeholder={form.lotSizeUnit === "acres" ? "0.25" : "5000"}
                disabled={globalLoading}
              />
              <Select
                id="lotSizeUnit"
                aria-label="Units"
                style={{ width: "auto" }}
                value={form.lotSizeUnit}
                onChange={(e) => updateField("lotSizeUnit", e.target.value)}
                disabled={globalLoading}
              >
                <option value="sqft">sqft</option>
                <option value="acres">acres</option>
              </Select>
            </div>
            {renderFieldError("lotSize")}
          </FieldGroup>
        </FieldRow>

        <FieldRow $columns="1fr">
          <FieldGroup>
            <Label htmlFor="email">Email address{signedIn ? "" : " *"}</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => updateField("email", e.target.value)}
              placeholder="you@brokerage.com"
              disabled={globalLoading}
            />
            <FieldHint>
              {signedIn && !form.email.trim() && account?.email
                ? `We'll email ${account.email} when your listing prep is ready — change it here to use another address.`
                : "We'll email you here when your listing prep is ready"}
            </FieldHint>
            {renderFieldError("email")}
          </FieldGroup>
        </FieldRow>

        <SectionDivider />

        <FieldRow $columns="1fr 1fr 1fr">
          <FieldGroup>
            <Label htmlFor="askingPrice">
              Asking price <OptionalBadge>Optional</OptionalBadge>
            </Label>
            <Input
              id="askingPrice"
              type="number"
              min="0"
              value={form.askingPrice}
              onChange={(e) => updateField("askingPrice", e.target.value)}
              placeholder="350000"
              disabled={globalLoading}
            />
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor="yearBuilt">
              Year built <OptionalBadge>Optional</OptionalBadge>
            </Label>
            <Input
              id="yearBuilt"
              type="number"
              value={form.yearBuilt}
              onChange={(e) => updateField("yearBuilt", e.target.value)}
              placeholder="1990"
              disabled={globalLoading}
            />
            {renderFieldError("yearBuilt")}
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor="homeType">
              Home type <OptionalBadge>Optional</OptionalBadge>
            </Label>
            <Select
              id="homeType"
              value={form.homeType}
              onChange={(e) => updateField("homeType", e.target.value)}
              disabled={globalLoading}
            >
              <option value="">Select</option>
              {HOME_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </FieldGroup>
        </FieldRow>

        <FieldRow $columns="1fr">
          <FieldGroup>
            <Label htmlFor="description">
              Listing description <OptionalBadge>Optional</OptionalBadge>
            </Label>
            <TextArea
              id="description"
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              placeholder="Already have a description? Paste it here. Otherwise, we'll generate one for you."
              disabled={globalLoading}
            />
          </FieldGroup>
        </FieldRow>

        <FieldRow $columns="1fr">
          <FieldGroup>
            <Label htmlFor="sellerNotes">
              Anything else Listino should know? <OptionalBadge>Optional</OptionalBadge>
            </Label>
            <TextArea
              id="sellerNotes"
              value={form.sellerNotes}
              onChange={(e) => updateField("sellerNotes", e.target.value)}
              placeholder="Seller goals, local insights, recent renovations, preferred tone, or anything else you'd like reflected in your listing prep."
              disabled={globalLoading}
            />
            <FieldHint>
              Have it in a document instead? You'll be able to upload inspections, disclosures, and
              floor plans on the next step — Listino reads them too.
            </FieldHint>
          </FieldGroup>
        </FieldRow>

        <SubmitButton type="submit" disabled={globalLoading || intakeCooldown.blocked}>
          {globalLoading ? "Creating listing…" : "Continue to photos"}
        </SubmitButton>

        <AltLink>
          Have a live listing? <Link to="/verify">Import it automatically</Link>
        </AltLink>
      </form>
    );
  };

  const renderStep2 = () => {
    const images = currentListing?.images || [];
    const hasImages = images.length > 0;

    return (
      <>
        <PropertySummary>
          <SummaryItem>
            {form.streetAddress}, {form.city}, {form.addressState}
          </SummaryItem>
          <SummaryItem>
            {form.bedrooms} bed / {form.bathrooms} bath
          </SummaryItem>
          <SummaryItem>{Number(form.livingArea).toLocaleString()} sqft</SummaryItem>
        </PropertySummary>

        <SectionLabel>Upload photos</SectionLabel>

        <ImageUploader
          listingUuid={listingUuid}
          images={images}
          onUpload={handleImageUpload}
          onDelete={handleImageDelete}
          uploading={imageUploadLoading}
          uploadProgress={uploadProgress}
          error={imageUploadError}
        />

        {!hasImages && (
          <FieldHint style={{ display: "block", marginTop: "12px" }}>
            No photos yet? That's okay. You can add them later.
          </FieldHint>
        )}

        {!hasImages && (
          <SkipPhotosRow>
            <button
              type="button"
              onClick={handleSkipPhotos}
              disabled={skippingPhotos || imageUploadLoading}
            >
              {skippingPhotos ? "Skipping photos…" : "Skip photos for now"}
            </button>
            {skipPhotosError && (
              <FieldError role="alert" style={{ display: "block", marginTop: "6px" }}>
                {skipPhotosError}
              </FieldError>
            )}
          </SkipPhotosRow>
        )}

        <SectionLabel style={{ marginTop: "28px" }}>Documents for the analysis</SectionLabel>

        <InputDocumentUploader
          listingUuid={listingUuid}
          documents={currentListing?.input_documents}
        />

        <SubmitButton type="button" onClick={handleContinueToReview} disabled={imageUploadLoading}>
          Continue to review
        </SubmitButton>

        <AltLink>
          Have a live listing? <Link to="/verify">Import it automatically</Link>
        </AltLink>
      </>
    );
  };

  const renderListingHydration = () => {
    if (!confirmationLoading && confirmationError) {
      return (
        <>
          <ErrorMessage role="alert">
            {flattenError(confirmationError).map(({ field, message }, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: normalized errors are rebuilt without reordering.
              <div key={i}>{field ? `${field}: ${message}` : message}</div>
            ))}
          </ErrorMessage>
          <SubmitButton type="button" onClick={handleRetryListingHydration}>
            Try again
          </SubmitButton>
        </>
      );
    }

    return (
      <PropertySummary>
        <output>Loading your listing details…</output>
      </PropertySummary>
    );
  };

  return (
    <PageContainer>
      <NewNavbar />
      <MainContent>
        <Container>
          <PageTitle>
            {step === 1 ? "Confirm the property details" : "Upload property photos"}
          </PageTitle>
          <PageSubtitle>
            {step === 1
              ? "Property details have been prepared from available records. Confirm any missing required fields, then add as much or as little as you'd like. The more you share, the more personalized your listing prep becomes."
              : "Quick phone photos or professional listing photos — both work. Listino personalizes staging recommendations, edits listing photos, and recommends the best photo order."}
          </PageSubtitle>

          <StepIndicator
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={2}
            aria-valuenow={step}
            aria-label={`Step ${step} of 2`}
          >
            <StepDotRow aria-hidden="true">
              <StepDot $active={step >= 1} />
              <StepDot $active={step >= 2} />
            </StepDotRow>
            <StepLabel>Step {step} of 2</StepLabel>
          </StepIndicator>

          <FormCard>
            {listingNeedsHydration
              ? renderListingHydration()
              : step === 1
                ? renderStep1()
                : renderStep2()}
          </FormCard>
        </Container>
      </MainContent>
      <NewFooter />
    </PageContainer>
  );
}

export default ManualSubmitPage;
